<template>
  <div class="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 border border-blueGray-200 rounded-lg">

    <!-- Header -->
    <div class="rounded-t mb-0 px-4 py-3 border-0">
      <div class="flex flex-wrap items-center justify-between">
        <div class="relative px-4">
          <h3 class="text-lg text-blueGray-700"><i class="pi pi-barcode mx-2 text-sm"></i>Products</h3>
        </div>
        <div class="flex flex-wrap items-center mb-3" style="gap: 0.25rem;">
          <button
            class="bg-blueGray-900 text-white active:bg-blueGray-800 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150 max-w-full"
            type="button"
            @click="(e) => $emit('initNewProduct', e)"
          >
            New Product
          </button>
        </div>
      </div>

      <!-- Filters -->
      <div class="flex flex-wrap items-center px-4" style="gap: 0.5rem;">
        <div class="relative flex-1" style="min-width: 160px;">
          <i class="fa fa-search text-blueGray-300 text-sm absolute" style="left:12px; top:50%; transform:translateY(-50%); pointer-events:none;"></i>
          <input
            v-model="search"
            @input="onFilterInput"
            type="text"
            placeholder="Search by product name..."
            style="padding-left: 32px;"
            class="w-full px-3 py-2 bg-blueGray-50 rounded-lg text-xs text-blueGray-600 placeholder-blueGray-300 border-0 focus:outline-none"
          />
        </div>

        <select
          v-model="categoryId"
          @change="applyFilters"
          :disabled="loaders().getItems"
          class="text-xs px-3 py-2 rounded border border-blueGray-200 text-blueGray-600 bg-white focus:outline-none"
        >
          <option value="">All categories</option>
          <option v-for="(cat, i) in (categories().data || [])" :key="i" :value="cat.id">{{ cat.name }}</option>
        </select>

        <input
          v-model="variant"
          @input="onFilterInput"
          type="text"
          placeholder="Filter by variant..."
          class="text-xs px-3 py-2 rounded border border-blueGray-200 text-blueGray-600 placeholder-blueGray-300 focus:outline-none"
          style="min-width: 140px;"
        />

        <select
          v-model="planId"
          @change="applyFilters"
          :disabled="loaders().getItems"
          class="text-xs px-3 py-2 rounded border border-blueGray-200 text-blueGray-600 bg-white focus:outline-none"
        >
          <option value="">All plans</option>
          <option v-for="(plan, i) in (plans() || [])" :key="i" :value="plan.id">{{ plan.name }}</option>
        </select>

        <!-- Grid / List toggle -->
        <div class="flex items-center bg-blueGray-100 rounded-lg p-1 gap-1 flex-shrink-0">
          <button
            type="button"
            class="flex items-center px-3 py-2 rounded-md text-xs focus:outline-none transition-all"
            :class="viewMode === 'grid' ? 'bg-white text-blueGray-700 shadow-sm' : 'text-blueGray-400 hover:text-blueGray-600'"
            title="Grid view"
            @click="viewMode = 'grid'"
          >
            <i class="fa fa-th-large"></i>
          </button>
          <button
            type="button"
            class="flex items-center px-3 py-2 rounded-md text-xs focus:outline-none transition-all"
            :class="viewMode === 'list' ? 'bg-white text-blueGray-700 shadow-sm' : 'text-blueGray-400 hover:text-blueGray-600'"
            title="List view"
            @click="viewMode = 'list'"
          >
            <i class="fa fa-list"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Product grid -->
    <div v-if="viewMode === 'grid'" class="block w-full overflow-x-auto">
      <div class="flex flex-wrap">

        <template v-if="items().data.length > 0">
          <div class="w-full sm:w-6/12 lg:w-3/12 px-4" v-for="(item, i) in items().data" :key="item.id || i">
            <div class="my-4 shadow-lg rounded-lg overflow-hidden border border-blueGray-100">

              <!-- Main image -->
              <div class="relative w-full bg-blueGray-50" style="height:200px;">
                <img
                  :src="activeThumb(item)"
                  class="w-full h-full object-cover"
                  :alt="item.name"
                />
                <!-- No-image overlay -->
                <div
                  v-if="!item.thumbnails || item.thumbnails.length === 0"
                  class="absolute inset-0 flex flex-col items-center justify-center text-blueGray-300"
                >
                  <i class="fa fa-image text-3xl mb-1"></i>
                  <span class="text-xs">No images</span>
                </div>
                <!-- Status badge -->
                <span
                  class="absolute top-2 left-2 text-xs font-bold px-2 py-0 rounded-full uppercase"
                  :class="{
                    'bg-emerald-500 text-white': item.status === 'PUBLISHED' || item.status === 'APPROVED',
                    'bg-orange-400 text-white':  item.status === 'PENDING',
                    'bg-red-500 text-white':     item.status === 'BLOCKED' || item.status === 'DECLINE',
                  }"
                >{{ item.status }}</span>
              </div>

              <!-- Thumbnail strip -->
              <div
                v-if="item.thumbnails && item.thumbnails.length > 0"
                class="flex px-2 py-2 gap-1 overflow-x-auto bg-blueGray-50 border-t border-blueGray-100"
                style="scrollbar-width:none;"
              >
                <button
                  v-for="(url, j) in item.thumbnails"
                  :key="j"
                  class="flex-shrink-0 rounded overflow-hidden focus:outline-none transition-all duration-150"
                  :class="activeIndex(item.id) === j
                    ? 'ring-2 ring-emerald-500 ring-offset-1'
                    : 'opacity-60 hover:opacity-100'"
                  style="width:44px; height:44px;"
                  @click="setActive(item.id, j)"
                >
                  <img :src="url" class="w-full h-full object-cover" :alt="`thumb ${j+1}`" />
                </button>
              </div>

              <!-- Info row -->
              <div class="px-4 py-3">
                <div class="flex justify-between items-start">
                  <div class="flex-1 min-w-0 mr-2">
                    <h6 class="text-sm font-semibold text-blueGray-700 truncate">{{ item.name }}</h6>
                    <p v-if="item.sub_title" class="text-xs text-blueGray-400 truncate">{{ item.sub_title }}</p>
                  </div>
                  <div class="text-right flex-shrink-0">
                    <p class="text-sm font-semibold text-blueGray-700">{{ formatter("NGN").format(item.price) }}</p>
                    <p class="text-xs text-blueGray-400">{{ item.quantity }} units</p>
                  </div>
                </div>
                <div class="flex justify-between items-center mt-2 pt-2 border-t border-blueGray-100">
                  <button
                    class="text-emerald-600 text-xs font-bold focus:outline-none"
                    @click="$emit('selectedProduct', item)"
                  >
                    <i class="fa fa-pen mr-1"></i> Edit
                  </button>
                  <span class="text-xs text-blueGray-400">
                    <i class="fa fa-image mr-1"></i>
                    {{ (item.thumbnails || []).length }} photo{{ (item.thumbnails || []).length !== 1 ? 's' : '' }}
                  </span>
                </div>
              </div>

            </div>
          </div>
        </template>

        <template v-else-if="loaders().getItems">
          <div class="w-full sm:w-6/12 lg:w-3/12 px-4" v-for="n in 6" :key="n">
            <div class="my-4 rounded-lg overflow-hidden border border-blueGray-100">
              <div class="w-full bg-blueGray-100 animate-pulse" style="height:200px;"></div>
              <div class="px-4 py-3">
                <div class="h-3 bg-blueGray-200 rounded animate-pulse w-3/4 mb-2"></div>
                <div class="h-3 bg-blueGray-200 rounded animate-pulse w-1/2 mb-3"></div>
                <div class="flex justify-between items-center mt-2 pt-2 border-t border-blueGray-100">
                  <div class="h-3 bg-blueGray-200 rounded animate-pulse w-1/4"></div>
                  <div class="h-3 bg-blueGray-200 rounded animate-pulse w-1/4"></div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="flex items-center justify-center w-full my-8 text-blueGray-400 text-sm">
            No products match your filters.
          </div>
        </template>

      </div>
    </div>

    <!-- Product list -->
    <div v-else class="block w-full overflow-x-auto">
      <table class="items-center w-full bg-transparent border-collapse">
        <thead>
          <tr>
            <th class="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100">Product</th>
            <th class="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100">Category</th>
            <th class="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100">Price</th>
            <th class="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100">Units</th>
            <th class="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100">Status</th>
            <th class="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100"></th>
          </tr>
        </thead>
        <tbody>
          <template v-if="items().data.length > 0">
            <tr v-for="(item, i) in items().data" :key="item.id || i">
              <th class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-left">
                <div class="flex items-center">
                  <img :src="activeThumb(item)" class="w-8 h-8 rounded object-cover flex-shrink-0 mr-2 bg-blueGray-50" :alt="item.name" />
                  <div class="min-w-0">
                    <span class="font-semibold block truncate" style="max-width: 220px;">{{ item.name }}</span>
                    <span v-if="item.sub_title" class="block text-blueGray-400 truncate" style="max-width: 220px;">{{ item.sub_title }}</span>
                  </div>
                </div>
              </th>
              <td class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                {{ item.category ? item.category.name : '—' }}
              </td>
              <td class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                {{ formatter("NGN").format(item.price) }}
              </td>
              <td class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                {{ item.quantity }}
              </td>
              <td class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                <span
                  class="text-xs font-bold px-2 py-1 rounded-full uppercase"
                  :class="{
                    'bg-emerald-500 text-white': item.status === 'PUBLISHED' || item.status === 'APPROVED',
                    'bg-orange-400 text-white':  item.status === 'PENDING',
                    'bg-red-500 text-white':     item.status === 'BLOCKED' || item.status === 'DECLINE',
                  }"
                >{{ item.status }}</span>
              </td>
              <td class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-right">
                <button
                  class="text-emerald-600 text-xs font-bold focus:outline-none"
                  @click="$emit('selectedProduct', item)"
                >
                  <i class="fa fa-pen mr-1"></i> Edit
                </button>
              </td>
            </tr>
          </template>
          <tr v-else-if="!loaders().getItems">
            <td colspan="6" class="text-center text-blueGray-400 text-sm p-8">No products match your filters.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { formatter } from '../../utils/formatter.util';
import placeholder from "@/assets/img/landscape-placeholder.svg";

export default {
  data() {
    return {
      search: "",
      categoryId: "",
      variant: "",
      planId: "",
      viewMode: "grid",
      debounceTimer: null,
      placeholder,
      activeIndices: {}, // { [itemId]: selectedThumbIndex }
    };
  },
  methods: {
    ...mapActions(["getItems", "getCategories", "getAllPlans"]),
    ...mapGetters(["items", "loaders", "categories", "plans"]),

    activeIndex(itemId) {
      return this.activeIndices[itemId] ?? 0;
    },

    activeThumb(item) {
      if (!item.thumbnails || item.thumbnails.length === 0) return this.placeholder;
      const idx = this.activeIndex(item.id);
      return item.thumbnails[idx] ?? item.thumbnails[0];
    },

    setActive(itemId, index) {
      this.activeIndices = { ...this.activeIndices, [itemId]: index };
    },

    applyFilters() {
      this.getItems({
        search: this.search || undefined,
        category: this.categoryId || undefined,
        variant: this.variant || undefined,
        plan_id: this.planId || undefined,
      });
    },

    onFilterInput() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(this.applyFilters, 400);
    },
  },
  mounted() {
    this.getItems();
    this.getCategories();
    // Products only ever attach to "commerce" plans — restrict the picker to those.
    this.getAllPlans({ q: '?type=commerce' });
  },
  created() {
    this.formatter = formatter;
  },
};
</script>

<style scoped>
/* Hide horizontal scrollbar on thumbnail strip while keeping it scrollable */
.overflow-x-auto::-webkit-scrollbar {
  display: none;
}
</style>
